/**
 * MKS-345 — the sales pipeline, edited in Admin.
 *
 * The leads board header gives the quick interactions (add a column, rename,
 * recolour, delete). This page is where the two properties that do not fit on
 * a board header live: a stage's KIND — which column means "won", which means
 * "lost" — and its win PROBABILITY, the percentage the weighted forecast
 * multiplies deal values by.
 *
 * The list itself is `StateListEditor`, the very component the ticket board's
 * project Task States section uses, with the kind/probability controls passed
 * through its `extraControls` slot. Drag to reorder, click the name to rename,
 * click the swatch to recolour, X to delete — identical interactions, because
 * it is identical code.
 */
import { useMemo } from 'react';
import { Spinner } from '../../components/common/Spinner';
import { StateListEditor, type EditorState } from '../../components/domain/StateListEditor';
import {
  useLeadStages,
  useCreateLeadStage,
  useUpdateLeadStage,
  useDeleteLeadStage,
  useReorderLeadStages,
} from '../../hooks/useLeadStages';
import {
  LEAD_STAGE_KINDS,
  LEAD_STAGE_KIND_LABELS,
  type LeadStageKind,
} from '../../services/api/leadStages';

export default function LeadStagesPage() {
  const { data, isLoading } = useLeadStages();
  const createStage = useCreateLeadStage();
  const updateStage = useUpdateLeadStage();
  const deleteStage = useDeleteLeadStage();
  const reorderStages = useReorderLeadStages();

  const stages = useMemo(() => data?.data ?? [], [data]);

  const editorStates: EditorState[] = useMemo(
    () =>
      stages.map((s) => ({
        id: s.id,
        name: s.name,
        ordering: s.ordering,
        color: s.color,
      })),
    [stages],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900">Pipeline stages</h1>
      <p className="mt-1 mb-6 text-sm text-gray-500">
        The columns of the leads board, shared by everyone. Drag to reorder, click a name to
        rename it, click a swatch to recolour it. A stage can only be deleted once no lead sits
        in it. Exactly one stage is the won stage and one is the lost stage — those two drive
        “Set up projects” and the presales archive, whatever they are called.
      </p>

      <StateListEditor
        mode="project"
        states={editorStates}
        onAdd={(name, color) => createStage.mutate({ name, color })}
        onRename={(id, name) => updateStage.mutate({ id, name })}
        onRecolor={(id, color) => updateStage.mutate({ id, color })}
        onDelete={(id) => deleteStage.mutate(id)}
        onReorder={(ids) => reorderStages.mutate(ids)}
        extraControls={(stageId) => {
          const stage = stages.find((s) => s.id === stageId);
          if (!stage) return null;
          return (
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor={`stage-kind-${stage.id}`}>
                Kind of {stage.name}
              </label>
              <select
                id={`stage-kind-${stage.id}`}
                value={stage.kind}
                // Claiming "won" or "lost" demotes whichever stage holds it
                // today — the server does that in one transaction, so the
                // pipeline is never briefly without one.
                onChange={(e) =>
                  updateStage.mutate({ id: stage.id, kind: e.target.value as LeadStageKind })
                }
                data-skip-rename="true"
                className="rounded border border-gray-300 px-1.5 py-0.5 text-xs"
              >
                {LEAD_STAGE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {LEAD_STAGE_KIND_LABELS[kind]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor={`stage-probability-${stage.id}`}>
                Win probability of {stage.name}
              </label>
              <div className="flex items-center gap-0.5">
                <input
                  id={`stage-probability-${stage.id}`}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  defaultValue={stage.probability}
                  data-skip-rename="true"
                  // Committed on blur rather than on every keystroke, so
                  // typing "75" does not PATCH a 7 on the way.
                  onBlur={(e) => {
                    const next = Number(e.currentTarget.value);
                    if (!Number.isInteger(next) || next < 0 || next > 100) {
                      e.currentTarget.value = String(stage.probability);
                      return;
                    }
                    if (next !== stage.probability) {
                      updateStage.mutate({ id: stage.id, probability: next });
                    }
                  }}
                  className="w-14 rounded border border-gray-300 px-1.5 py-0.5 text-xs"
                />
                <span className="text-xs text-gray-400">%</span>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}
